import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { RequestIdentity } from './http-collector.interceptor';

type PseudoPromise<K> = {
  then: (fn: (e: K) => Promise<K>) => Promise<K>;
  catch: (fn: (e: K) => Promise<K>) => Promise<K>;
};

interface ResponseData {
  readonly requestIdentity: RequestIdentity;
  readonly data?: any;
  readonly error?: ErrorData;
  readonly status: number;
  readonly loading: boolean;
}

interface ErrorData {
  readonly message: string;
  readonly errors: Record<string, string[]>;
  readonly details: Record<string, string[]>;
}

@Injectable({
  providedIn: 'root',
})
export class HttpStoreService {
  readonly data = new Map<string, ResponseData[]>();

  private readonly requestStatus = new Subject<ResponseData>();

  // private _loading = new BehaviorSubject<{ tag: string; value: boolean }>({
  //   tag: '',
  //   value: false,
  // });
  private _activeRequestsCount = new BehaviorSubject<number>(0);
  readonly activeRequestsCount$ = this._activeRequestsCount.asObservable();

  get activeRequestsCount() {
    return this._activeRequestsCount.value;
  }

  // FIXME: Experimental
  observe(...tags: string[]) {
    return this.requestStatus.pipe(
      filter((e) => tags.includes(e.requestIdentity.tag)),
      map((e) => {
        const key = tags.find((tag) => this.data.get(tag) !== undefined);
        if (!key) {
          return false;
        }
        const values = this.data.get(key);
        if (!values) {
          return false;
        }
        return values.some((value) => value.loading);
      }),
    );
  }

  isLoading(...tags: string[]) {
    return tags.some((tag) => {
      const values = this.data.get(tag);
      return values?.some((value) => value.loading);
    });
  }

  getErrors(tag: string) {
    const values = this.data.get(tag);
    if (!values) {
      return null;
    }
    return values[0].error;
  }

  wrapPromise<K, T extends Promise<K> | PseudoPromise<K>>(
    requestIdentity: RequestIdentity,
    promise: T,
  ): T {
    this.requestStarted(requestIdentity);
    return promise
      .then((e) => {
        this.requestSuccess(requestIdentity, e);
        return Promise.resolve(e);
      })
      .catch((error) => {
        this.requestFailed(requestIdentity, error);
        return Promise.reject(error);
      }) as T;
  }

  wrap<K, T extends Observable<K>>(
    requestIdentity: RequestIdentity,
    obs: T,
  ): T {
    this.requestStarted(requestIdentity);
    return obs.pipe(
      filter((e: any) => e.type !== 0),
      tap((e) => this.requestSuccess(requestIdentity, e)),
      catchError((error) => {
        this.requestFailed(requestIdentity, error);
        return throwError(error);
      }),
    ) as T;
  }

  requestStarted(requestIdentity: RequestIdentity) {
    const data = { requestIdentity, loading: true, status: 0 };
    if (this.data.get(requestIdentity.tag) === undefined)
      this.data.set(requestIdentity.tag, []);
    this.data.get(requestIdentity.tag)!.push(data);
    this.requestStatus.next(data);
    this._activeRequestsCount.next(this.activeRequestsCount + 1);
  }

  requestSuccess(requestIdentity: RequestIdentity, data: any) {
    const value = this.data
      .get(requestIdentity.tag)
      ?.find((e) => e.requestIdentity.uuid === requestIdentity.uuid);
    const newData: ResponseData = value
      ? {
          ...value,
          status: 200,
          loading: false,
          data,
        }
      : { loading: false, status: 200, requestIdentity, data };

    const newValues =
      this.data.get(requestIdentity.tag)?.map((e) => {
        if (e.requestIdentity.uuid === requestIdentity.uuid) return newData;
        return e;
      }) ?? [];
    this.data.set(requestIdentity.tag, newValues);
    this.requestStatus.next(newData);
    this.clearRequest(requestIdentity);
  }

  requestFailed(requestIdentity: RequestIdentity, error: any) {
    const value = this.data
      .get(requestIdentity.tag)
      ?.find((e) => e.requestIdentity.uuid === requestIdentity.uuid);
    const newData: ResponseData = value
      ? {
          ...value,
          status: error?.status ?? 0,
          loading: false,
          error: error?.error ?? error,
        }
      : { loading: false, status: 0, requestIdentity, error };

    const newValues =
      this.data.get(requestIdentity.tag)?.map((e) => {
        if (e.requestIdentity.uuid === requestIdentity.uuid) return newData;
        return e;
      }) ?? [];
    this.data.set(requestIdentity.tag, newValues);
    this.requestStatus.next(newData);
    this.clearRequest(requestIdentity);
  }

  private clearRequest(requestIdentity: RequestIdentity) {
    const values = this.data.get(requestIdentity.tag);
    if (values) {
      const newValues = values.filter(
        (e) => e.requestIdentity.uuid !== requestIdentity.uuid,
      );
      if (newValues.length === 0) {
        this.data.delete(requestIdentity.tag);
      } else {
        this.data.set(requestIdentity.tag, newValues);
      }
    }
    this._activeRequestsCount.next(this.activeRequestsCount - 1);
  }
}
